import { EmailSchema } from "@gosaas/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";
import { AuthPage } from "@/components/auth/auth-page";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/auth/password-reset")({
	component: PasswordReset,
	head: () =>
		seo({
			title: "Password Reset",
			description: "Reset your password",
			keywords: "password, reset, forgot, forgot password",
		}),
});

export function PasswordReset() {
	const router = useRouter();
	const form = useForm<z.infer<typeof EmailSchema>>({
		resolver: zodResolver(EmailSchema),
		defaultValues: {
			email: "",
		},
	});

	const onSubmit = async (data: z.infer<typeof EmailSchema>) => {
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.authPasswordResetCreate({
					email: data.email,
				}),
			);
			toast.success("Password reset email sent", {
				description: "Please check your email for further instructions.",
			});
			router.navigate({
				to: "/auth/reset-password",
				search: {
					email: data.email,
				},
			});
		} catch (error) {
			toast.error("Couldn't send the reset link", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	return (
		<AuthPage
			title="Reset password"
			description="We’ll email you a link to choose a new password."
		>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-5"
					noValidate
				>
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Email</FormLabel>
								<FormControl>
									<Input
										{...field}
										type="email"
										required
										autoComplete="email"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<Button type="submit" className="h-11 w-full cursor-pointer">
						Send reset link
					</Button>
				</form>
			</Form>

			<p className="mt-8 text-center text-base text-muted-foreground">
				<Link
					to="/auth/login"
					className="font-medium text-foreground underline-offset-4 hover:underline"
				>
					Back to sign in
				</Link>
			</p>
		</AuthPage>
	);
}
